'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { MessageSquare, Calendar, Users, LayoutDashboard, Kanban } from 'lucide-react';

// Bottom tab bar — mobile-only navigation that replaces the hamburger menu.
// Five tabs covering the field-operator daily loop: the ops dashboard (home),
// chat with the agent, work the pipeline, look up customers, plan the day.
// Hub and Settings live behind icons in the mobile topbar (rare-use, don't
// deserve tab slots).
//
// Hidden on the workflow editor and on public pages (login/welcome) — those
// have their own chrome.

const TABS = [
  { href: '/operations', label: 'Dashboard', icon: LayoutDashboard },
  { href: '/agent',      label: 'Agent',     icon: MessageSquare },
  { href: '/pipeline',   label: 'Pipeline',  icon: Kanban },
  { href: '/customers',  label: 'Customers', icon: Users },
  { href: '/schedule',   label: 'Schedule',  icon: Calendar },
];

const HIDE_ON = [
  '/login',
  '/signup',
  '/welcome',
  '/onboarding',
  '/auth',
  '/privacy',
  '/terms',
  '/affiliates',
  '/reset-password',
];

export default function MobileTabBar() {
  const pathname = usePathname();

  // Hide on public/auth pages and on the workflow editor (which has its own
  // chrome and benefits from full-height canvas space).
  const isHidden = HIDE_ON.some(p => pathname === p || pathname.startsWith(p + '/'))
    || (pathname.startsWith('/workflows/') && pathname !== '/workflows');

  if (isHidden) return null;

  return (
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-[--bg-card]/95 backdrop-blur-xl border-t border-[--border-primary]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      aria-label="Primary"
    >
      <ul className="flex">
        {TABS.map(({ href, label, icon: Icon }) => {
          const active = pathname === href || pathname.startsWith(href + '/');
          return (
            <li key={href} className="flex-1">
              <Link
                href={href}
                className={`flex flex-col items-center justify-center gap-0.5 py-2 min-h-[56px] transition-colors ${
                  active
                    ? 'text-[#00C2A8]'
                    : 'text-[--text-tertiary] active:text-[--text-primary]'
                }`}
                aria-current={active ? 'page' : undefined}
              >
                <Icon className="w-[22px] h-[22px]" strokeWidth={active ? 2.5 : 2} />
                <span className="text-[10px] font-semibold tracking-tight">{label}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
